// The assistant backend answers 429 or 503 when Clerk could not confirm the
// caller's access in time (QA 2026-10-12). That is not an error to show: the
// pollers back off (growing per failure in a row, honouring Retry-After,
// capped) and keep polling.
const TRANSIENT_STATUSES = new Set([429, 503]);
const TRANSIENT_CODES = new Set(['TOO_MANY_REQUESTS', 'SERVICE_UNAVAILABLE']);
export const MIN_TRANSIENT_BACKOFF_MS = 1_000;
export const MAX_TRANSIENT_BACKOFF_MS = 5_000;

type TrpcClientErrorLike = {
  data?: { httpStatus?: unknown; code?: unknown } | null;
  meta?: { response?: { status?: unknown; headers?: { get?: (name: string) => string | null } } };
};

function errorStatus(error: TrpcClientErrorLike) {
  const status = error.data?.httpStatus ?? error.meta?.response?.status;
  return typeof status === 'number' ? status : null;
}

export function isTransientPollError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as TrpcClientErrorLike;
  const status = errorStatus(candidate);
  if (status !== null && TRANSIENT_STATUSES.has(status)) return true;
  return typeof candidate.data?.code === 'string' && TRANSIENT_CODES.has(candidate.data.code);
}

/**
 * How long to wait before the next poll after the `consecutiveFailures`-th
 * 429/503 in a row: 1 s, doubling each time, at most 5 s, and never less than
 * the server's Retry-After. Null for any other error. Callers reset their
 * count on a success, so a busy server is polled less, not at the usual cadence.
 */
export function transientPollDelayMs(error: unknown, consecutiveFailures: number): number | null {
  if (!isTransientPollError(error)) return null;
  const header = (error as TrpcClientErrorLike).meta?.response?.headers?.get?.('retry-after');
  const seconds = header ? Number(header) : NaN;
  const retryAfterMs = Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : 0;
  const grown = MIN_TRANSIENT_BACKOFF_MS * 2 ** Math.max(0, consecutiveFailures - 1);
  return Math.min(Math.max(retryAfterMs, grown), MAX_TRANSIENT_BACKOFF_MS);
}
